"use client";

import { resolveTrustedReturnTo, useAuth } from "@pex/auth/client";
import Link from "next/link";
import { useEffect, useState } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { OtpStep } from "./otp-step";

const AGE_AFFIRMATION_ERROR =
  "You must confirm you are 18 or older to sign up.";

const RESEND_COOLDOWN = 60;

function formatSendError(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  return "An error occurred";
}

function formatVerifyError(err: unknown): string {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes("expired") || msg.includes("invalid")) {
      return "Invalid or expired code. Request a new one below.";
    }
    return err.message;
  }
  return "An error occurred";
}

function GoogleIcon() {
  return (
    <svg
      aria-hidden="true"
      className="mr-2 h-4 w-4"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function SignUpForm({
  className,
  initialEmail,
  returnTo,
  ...props
}: React.ComponentPropsWithoutRef<"div"> & {
  initialEmail?: string;
  returnTo?: string;
}) {
  const [step, setStep] = useState<"email" | "otp">("email");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(initialEmail ?? "");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [token, setToken] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { sendOtp, verifyOtp, signInWithGoogle } = useAuth();

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleGoogleSignIn = async () => {
    // Path A age gate (#1006): Google OAuth also creates an account, so the
    // affirmation must gate it too. (Google can't carry our metadata, so no
    // age_confirmed is persisted on this path — the block is the guarantee.)
    if (!ageConfirmed) {
      setSendError(AGE_AFFIRMATION_ERROR);
      return;
    }
    setIsGoogleLoading(true);
    setSendError(null);
    try {
      const { error } = await signInWithGoogle(returnTo);
      if (error) {
        throw error;
      }
    } catch (err: unknown) {
      setSendError(formatSendError(err));
      setIsGoogleLoading(false);
    }
  };

  const doSendOtp = async () => {
    // Path A age gate (#1006): block account creation unless the user has
    // affirmed they are 18+. Persisted as user metadata (no DOB) for audit.
    if (!ageConfirmed) {
      setSendError(AGE_AFFIRMATION_ERROR);
      return;
    }
    setIsSending(true);
    setSendError(null);
    try {
      const { error } = await sendOtp(email, {
        first_name: firstName,
        last_name: lastName,
        age_confirmed: true,
        age_confirmed_at: new Date().toISOString(),
      });
      if (error) {
        throw error;
      }
      setToken("");
      setVerifyError(null);
      setStep("otp");
      setCooldown(RESEND_COOLDOWN);
    } catch (err: unknown) {
      setSendError(formatSendError(err));
    } finally {
      setIsSending(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    await doSendOtp();
  };

  const handleResend = async () => {
    setToken("");
    setVerifyError(null);
    await doSendOtp();
  };

  const handleTokenChange = async (value: string) => {
    setToken(value);
    if (value.length !== 6 || isVerifying) {
      return;
    }
    setIsVerifying(true);
    setVerifyError(null);
    try {
      const { error } = await verifyOtp(email, value);
      if (error) {
        throw error;
      }
      window.location.href =
        resolveTrustedReturnTo(returnTo) ??
        process.env.NEXT_PUBLIC_WEBSITE_URL ??
        "/";
    } catch (err: unknown) {
      setVerifyError(formatVerifyError(err));
      setToken("");
      setIsVerifying(false);
    }
  };

  const loginParams = new URLSearchParams();
  if (returnTo) {
    loginParams.set("return_to", returnTo);
  }
  const loginQuery = loginParams.toString();
  const loginHref = loginQuery ? `/login?${loginQuery}` : "/login";

  if (step === "otp") {
    return (
      <OtpStep
        className={cn("flex flex-col gap-6", className)}
        cooldown={cooldown}
        email={email}
        isSending={isSending}
        isVerifying={isVerifying}
        onChangeEmail={() => {
          setStep("email");
          setToken("");
          setVerifyError(null);
          setSendError(null);
        }}
        onResend={handleResend}
        onTokenChange={handleTokenChange}
        signingInMessage="Creating your account…"
        token={token}
        verifyError={verifyError}
      />
    );
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="ring-0">
        <CardHeader>
          <CardTitle className="text-2xl">Create an account</CardTitle>
          <CardDescription>
            We&apos;ll send a sign-in code to your email
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <form onSubmit={handleSignUp}>
            <div className="flex flex-col gap-4">
              <div className="grid gap-2">
                <Label htmlFor="first-name">First name</Label>
                <Input
                  id="first-name"
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  required
                  type="text"
                  value={firstName}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="last-name">Last name</Label>
                <Input
                  id="last-name"
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  required
                  type="text"
                  value={lastName}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="m@example.com"
                  required
                  type="email"
                  value={email}
                />
                <div className="min-h-5">
                  {sendError && (
                    <p className="text-destructive text-sm">{sendError}</p>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox
                  aria-invalid={sendError === AGE_AFFIRMATION_ERROR}
                  checked={ageConfirmed}
                  className="mt-0.5"
                  id="age-affirmation"
                  onCheckedChange={(checked) => {
                    setAgeConfirmed(checked === true);
                    if (
                      checked === true &&
                      sendError === AGE_AFFIRMATION_ERROR
                    ) {
                      setSendError(null);
                    }
                  }}
                />
                <Label
                  className="font-normal text-sm leading-snug"
                  htmlFor="age-affirmation"
                >
                  I confirm I am 18 years of age or older.
                </Label>
              </div>
              <Button
                className="w-full"
                disabled={isSending || !ageConfirmed}
                type="submit"
              >
                {isSending ? "Sending code…" : "Continue with email"}
              </Button>
              <p className="text-balance text-center text-muted-foreground text-xs">
                By signing up you agree to our{" "}
                <a
                  className="underline underline-offset-4"
                  href={`${process.env.NEXT_PUBLIC_WEBSITE_URL}/legal/terms-service`}
                  rel="noopener"
                  target="_blank"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  className="underline underline-offset-4"
                  href={`${process.env.NEXT_PUBLIC_WEBSITE_URL}/legal/privacy-policy`}
                  rel="noopener"
                  target="_blank"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </form>
          {process.env.NEXT_PUBLIC_GOOGLE_AUTH_ENABLED === "true" && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>
              <Button
                className="w-full"
                disabled={isGoogleLoading || isSending || !ageConfirmed}
                onClick={handleGoogleSignIn}
                type="button"
                variant="outline"
              >
                <GoogleIcon />
                {isGoogleLoading ? "Redirecting…" : "Continue with Google"}
              </Button>
            </>
          )}
          <div className="text-center text-sm">
            Already have an account?{" "}
            <Link className="underline underline-offset-4" href={loginHref}>
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
